import * as React from 'react';
import { Link } from 'react-router-dom';

import { usePlanLimitsQuery } from 'src/api/queries/subscriptionQueries';
import { TPlanLimits } from 'src/api/functions/subscriptionApi';

import styles from './EntitlementMeter.module.scss';

const SUBSCRIPTION_ROUTE = '/admin/subscription';

/** Dimensions `GET /api/v1/limits` reports. */
export type EntitlementDimension = keyof TPlanLimits;

/**
 * `?purchase=` deep links the Subscription page already honours, so "add
 * capacity" lands on the opened buy control rather than the top of the page.
 * A dimension absent here has no self-serve purchase and falls back to
 * "Compare plans".
 */
const PURCHASE_DEEP_LINK: Partial<Record<EntitlementDimension, string>> = {
  asset_schemas: `${SUBSCRIPTION_ROUTE}?purchase=schemas`,
};

interface EntitlementMeterProps {
  dimension: EntitlementDimension;
  /** e.g. "schema" / "schemas". Used verbatim in the sentence. */
  noun: string;
  nounPlural: string;
  /**
   * Rendered under the sentence when the customer is NOT at the cap — the
   * place to explain what the number does and does not count.
   */
  hint?: string;
  /** Hide the upgrade/purchase link (e.g. for a non-owner who cannot act). */
  hideAction?: boolean;
  className?: string;
}

/**
 * "3 of 5 schemas used" / "All 1 schema in use · Add schemas — $5/mo".
 *
 * One component so every capped dimension counts and phrases itself the same
 * way, and so the upsell is wired everywhere at once instead of per page.
 *
 * Three rules it exists to enforce:
 *
 *  1. **Never render `used of quota` once `used >= quota`.** "2 of 1 schema
 *     slots used" is arithmetic nonsense on the page, and it is reachable:
 *     the cap is enforced on create only, so any plan change that LOWERS a
 *     cap leaves the account legitimately above it.
 *  2. **A ceiling always comes with a next step.** At the cap the sentence is
 *     followed by the actual action — buy capacity where the plan sells it
 *     (`max_extra > 0`), compare plans where it does not. A dead end with no
 *     price attached is not an upgrade decision, it is a wall.
 *  3. **Say the noun, not "slot".** "Slot" is our word for a row in
 *     `plan_quotas`; the customer has schemas.
 *
 * Renders nothing on an unlimited plan (`quota === 0`, the project's
 * convention) or before the numbers arrive — there is no honest sentence to
 * write in either case, and a flash of "0 of 0" is worse than silence.
 */
export const EntitlementMeter: React.FC<EntitlementMeterProps> = ({
  dimension,
  noun,
  nounPlural,
  hint,
  hideAction,
  className,
}) => {
  const { data: limits } = usePlanLimitsQuery();
  const limit = limits?.[dimension];

  if (!limit || limit.quota <= 0) return null;

  const { used, quota } = limit;
  const atCap = used >= quota;
  const overCap = used > quota;
  const word = quota === 1 ? noun : nounPlural;

  // `max_extra` only exists on dimensions that sell capacity (asset_schemas
  // today, migration 54). Anything else upgrades.
  const maxExtra = (limit as { max_extra?: number }).max_extra ?? 0;
  const canPurchase = maxExtra > 0 && Boolean(PURCHASE_DEEP_LINK[dimension]);

  const sentence = overCap
    ? `${used} ${used === 1 ? noun : nounPlural} active — your plan includes ${quota}`
    : atCap
      ? `All ${quota} ${word} in use`
      : `${used} of ${quota} ${word} used`;

  return (
    <div className={[styles.meter, className].filter(Boolean).join(' ')}>
      <span
        className={atCap ? styles.countAtCap : styles.count}
        data-test={`entitlement-meter-${dimension}`}
      >
        {sentence}
      </span>

      {atCap && !hideAction && (
        <>
          <span className={styles.separator} aria-hidden="true">·</span>
          <Link
            className={styles.action}
            to={canPurchase ? PURCHASE_DEEP_LINK[dimension]! : SUBSCRIPTION_ROUTE}
          >
            {canPurchase ? `Add ${nounPlural}` : 'Compare plans'}
          </Link>
        </>
      )}

      {!atCap && hint && <span className={styles.hint}>{hint}</span>}
    </div>
  );
};

export default EntitlementMeter;
